import { IUISupplier } from '@bridebook/toolbox/src/types';
import {
  LostLeadStorage,
  LostLeadStorageType,
} from '@bridebook/web/lib/storage-manager/basic/supplier-interest-confirmation';
import { mergeDeepRight } from '@bridebook/models/source/helpers/mergeDeepRight';
import gazetteer, { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { ISupplier } from '@bridebook/models/source/models/Suppliers.types';
import { getBookedVenue } from '@bridebook/web/lib/shortlist/selectors';
import { IApplicationState, TUIShortlistSupplier } from '@bridebook/web/lib/types';
import { format, isValid, parseISO, startOfDay } from 'date-fns';
import { SentryMinimal } from '@bridebook/toolbox/src/sentry';
import { AnyAction, Store } from '@reduxjs/toolkit';
import {
  openedLostLeadModalAnalytics,
  selectedLostLeadFeedbackAnalytics,
  submittedLostLeadFeedbackAnalytics,
} from '@bridebook/web/lib/lost-lead/analytics/actions';
import { toggleLostLeadModal } from '@bridebook/web/lib/ui/actions';
import type { ISupplier as IWeddingSupplier } from '@bridebook/models/source/models/Weddings/Suppliers.types';
import { ICountry_Supplier } from '@bridebook/models/source/models/Countries.types';

type SupportedSupplier = TUIShortlistSupplier | IWeddingSupplier | IUISupplier | ISupplier;

export interface ILostLeadParams {
  supplierTier?: number;
  supplierType?: IUISupplier['type'] | ISupplier['type'];
  supplierId?: string;
  countryCode?: CountryCodes;
}

/**
 * Manages lost lead functionality based on supplier context, daily limits, and user behavior
 * tracking.
 *
 * IMPORTANT: Call initialize() before using the service to ensure proper user-scoped data loading.
 * IMPORTANT: Call setGetState() to provide state access for checking booked venues.
 */
export class LostLeadService {
  private confirmationStorage: LostLeadStorageType = {};
  private store?: Store<IApplicationState, AnyAction>;
  private supplierCache = new Map<string, SupportedSupplier>();

  /**
   * Determines lost lead feature availability based on supplier tier, country, daily limits, and
   * venue booking status. Caches the supplier data for later use by other methods.
   */
  checkAvailability(params: ILostLeadParams, supplier: SupportedSupplier): boolean {
    if (!this.validateParams(params) || !this.store) {
      return false;
    }

    const { supplierId, supplierTier, countryCode } = params;
    const supplierType = Array.isArray(params.supplierType)
      ? params.supplierType[0]
      : params.supplierType;
    const limit = this.getLostLeadTrackingSetting(params.countryCode, supplierType);

    if (!limit) {
      return false;
    }

    // Cache supplier for later use
    supplier && this.cacheSupplier(supplierId, supplier);

    const market = gazetteer.getMarketByCountry(countryCode);
    const hasNotBeenShownYet = !this.confirmationStorage?.suppliersShown?.[supplierId];
    const minTier = market.getSettingRequiredTier('lostLeadTracking', supplierType);
    const meetsRequiredTier = supplierTier >= minTier;
    const isDailyLimitOk = this.checkDailyLimit(limit);
    const bookedVenue = getBookedVenue(this.store.getState());
    const isSupplierBooked = bookedVenue?.id === supplierId;

    return hasNotBeenShownYet && meetsRequiredTier && isDailyLimitOk && !isSupplierBooked;
  }

  /**
   * Loads user-scoped data from storage. Must be called before using other methods.
   */
  async initialize(): Promise<void> {
    try {
      const data = await LostLeadStorage.get();
      this.confirmationStorage = data ?? {};
    } catch (error) {
      this.confirmationStorage = {};
      SentryMinimal().captureException(error, {
        tags: {
          source: 'LostLeadService',
          method: 'initialize',
        },
      });
    }
  }

  /**
   * Opens the lost lead modal and tracks the event.
   * @param supplier - The supplier to open the modal for.
   * @param location - The location where the modal was opened from (for analytics).
   */
  openLostLeadModal(supplierId: string, location: Location): void {
    const storeData = this.getStoreStateAndDispatch();
    if (!storeData) {
      return;
    }

    const { dispatch } = storeData;

    const supplier = this.getSupplierDetails(supplierId);

    if (!supplier) {
      return;
    }

    dispatch(toggleLostLeadModal({ show: true, supplierId }));
    dispatch(openedLostLeadModalAnalytics(location, supplier));
  }

  /**
   * Records supplier interaction, updates daily count and timestamp.
   */
  recordInteraction(supplierId: string): void {
    const now = new Date();
    const nowISO = now.toISOString();
    const todayKey = this.getCurrentDateKey();

    const lastShown = this.confirmationStorage.lastShown;
    const lastShownDateKey = lastShown ? this.getDateKeyFromString(lastShown) : null;

    // Increment today's count or reset for new day
    const newCount = lastShownDateKey === todayKey ? (this.confirmationStorage.count ?? 0) + 1 : 1;

    this.updateStorage({
      suppliersShown: {
        [supplierId]: true as boolean,
      },
      count: newCount,
      lastShown: nowISO,
    });
  }

  /**
   * Provides Redux state access for venue booking checks.
   */
  setStore(store: Store<IApplicationState, AnyAction>): void {
    this.store = store;
  }

  trackLostLead(supplierId: string, selectedOptions: string[], text?: string): void {
    const storeData = this.getStoreStateAndDispatch();
    const supplier = this.getSupplierDetails(supplierId);

    if (!storeData || !supplier) {
      return;
    }

    const { dispatch } = storeData;

    selectedOptions.forEach((option) =>
      dispatch(selectedLostLeadFeedbackAnalytics(option, 'preSetOption', supplier)),
    );
    if (text && text.trim().length > 0) {
      dispatch(selectedLostLeadFeedbackAnalytics(text, 'text', supplier));
    }
    dispatch(submittedLostLeadFeedbackAnalytics(supplier));
  }

  /**
   * Caches supplier data for later use to avoid repeated store lookups.
   */
  private cacheSupplier(supplierId: string, supplier: SupportedSupplier): void {
    this.supplierCache.set(supplierId, supplier);
  }

  /**
   * Checks if daily appearance limit has been exceeded.
   */
  private checkDailyLimit(limit: number): boolean {
    const todayKey = this.getCurrentDateKey();
    const lastShown = this.confirmationStorage.lastShown;
    const currentCount = this.confirmationStorage.count ?? 0;

    if (!lastShown) {
      return true;
    }

    const lastShownDateKey = this.getDateKeyFromString(lastShown);

    // Reset count for new day or invalid dates
    if (!lastShownDateKey || lastShownDateKey !== todayKey) {
      return true;
    }

    // Check today's count against limit
    return currentCount < limit;
  }

  /**
   * Returns today's date key normalized to start of day.
   */
  private getCurrentDateKey(): string {
    return format(startOfDay(new Date()), 'yyyy-MM-dd');
  }

  /**
   * Converts ISO date string to normalized date key.
   */
  private getDateKeyFromString(dateString: string): string | null {
    const date = this.parseAndValidateDate(dateString);
    if (!date) {
      return null;
    }
    return format(startOfDay(date), 'yyyy-MM-dd');
  }

  /**
   * Gets state and dispatch from store, logging error if unavailable.
   * @returns Object with state and dispatch, or null if unavailable.
   */
  private getStoreStateAndDispatch(): { state: IApplicationState; dispatch: any } | null {
    const state = this.store?.getState();
    const dispatch = this.store?.dispatch;

    if (!state || !dispatch) {
      SentryMinimal().captureException('LostLeadService: No state found', {
        tags: {
          source: 'LostLeadService',
        },
      });
      return null;
    }

    return { state, dispatch };
  }

  /**
   * Gets supplier details from internal cache.
   * @param supplierId - The supplier ID to look up.
   * @returns The supplier object or null if not found in cache.
   */
  private getSupplierDetails(supplierId: string): SupportedSupplier | null {
    const cachedSupplier = this.supplierCache.get(supplierId);

    if (!cachedSupplier) {
      SentryMinimal().captureException('LostLeadService: No supplier found in cache', {
        tags: {
          source: 'LostLeadService',
        },
      });
      return null;
    }

    return cachedSupplier;
  }

  /**
   * Safely parses ISO date strings with validation.
   */
  private parseAndValidateDate(dateString: string): Date | null {
    try {
      const parsedDate = parseISO(dateString);
      return isValid(parsedDate) ? parsedDate : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Updates storage in memory and persists changes with error handling.
   */
  private updateStorage(data: LostLeadStorageType): void {
    try {
      this.confirmationStorage = mergeDeepRight(this.confirmationStorage, data);
      LostLeadStorage.set(this.confirmationStorage);
    } catch (error) {
      SentryMinimal().captureException(error, {
        tags: {
          source: 'LostLeadService',
          method: 'updateStorage',
        },
        extra: {
          data,
          currentStorage: this.confirmationStorage,
        },
      });
    }
  }

  /**
   * Type guard that validates all required parameters are present.
   */
  private validateParams(params: ILostLeadParams): params is Required<ILostLeadParams> {
    return !!(
      params.countryCode &&
      params.supplierId &&
      params.supplierType &&
      params.supplierTier !== undefined &&
      params.supplierTier !== null
    );
  }

  private getLostLeadTrackingSetting(countryCode: CountryCodes, supplierType: ICountry_Supplier) {
    const market = gazetteer.getMarketByCountry(countryCode);
    const setting = market.getSettingForType('lostLeadTracking', supplierType);
    return setting;
  }
}

type Location =
  | 'QuickResponse'
  | 'Unfavouriting'
  | 'Archiving a conversation'
  | '“Not the one” in shortlist';
